import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { alias, or, readOnly } from '@ember/object/computed';
import { isPresent } from '@ember/utils';
import { task } from 'ember-concurrency';

import RouterService from '@ember/routing/router-service';
import FormValidations from 'cio/utils/form-validations';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import PushService from 'cio/services/push';
import NewsletterModel from 'cio/models/newsletter';
import AnalyticsService from 'cio/services/analytics';

// not importing a test file, only app one with unfortunate "-test" filename suffix:
// eslint-disable-next-line ember/no-test-import-export
import NewsletterValidations from 'cio/utils/validations/newsletter-test';
import { waitForPromise } from '@ember/test-waiters';

const DEVICE_OPTIONS = [
  {
    label: 'Android',
    value: 'android',
  },
  {
    label: 'iOS',
    value: 'ios',
  },
];

export default class IndexController extends Controller {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service router!: RouterService;
  @service push!: PushService;

  @tracked isCreatingTest = false;
  @tracked hasTestSetup = false;

  @readOnly('model') newsletter!: NewsletterModel;
  @or('hasTestSetup', 'isCreatingTest') isTesting!: boolean;
  @readOnly('newsletter.hasDirtyAttributes') isDirty!: boolean;
  @readOnly('validations.hasClientErrors') isInvalid!: boolean;
  @alias('newsletter.channelInfo.canSplit') canSplit!: boolean;

  validations!: FormValidations;

  invalidErrorMessage = 'Your newsletter setup has some errors. Please fix them before moving ahead';

  constructor() {
    super(...arguments);

    this.set('validations', FormValidations.create({ validations: NewsletterValidations, context: this }));
  }

  get deviceOptions() {
    let enabledPlatforms = this.push.platformNames;
    return isPresent(enabledPlatforms)
      ? [
          {
            label: 'All',
            value: 'all',
          },
          ...DEVICE_OPTIONS.filter((opt) => enabledPlatforms.includes(opt.value)),
        ]
      : [];
  }

  get showDeviceOptions() {
    // if there are more options than the default "all" + either "android/ios"
    return this.newsletter.channel === 'push' && this.deviceOptions.length > 2;
  }

  @computed('newsletter.channel', 'canSplit')
  get blankslateTitle() {
    return this.canSplit || !this.newsletter.channel || this.newsletter.activeTemplates.length === 0
      ? 'No test yet'
      : `A/B tests aren't available for ${this.newsletter.channelInfo.name} Newsletters.`;
  }

  updateChannelTask = task(async (channel: NewsletterModel['channel']) => {
    try {
      this.newsletter.setProperties({
        channel,
        updateType: 'channel',
      });

      await this.newsletter.save();
    } catch {
      return this.newsletter.rollbackAttributes();
    }

    await this.newsletter.hasMany('templates').reload();
  });

  _deleteTest = task(async () => {
    let { newsletter } = this;

    try {
      newsletter.rollbackAttributes();

      await newsletter.deleteTest({});
      await newsletter.reload();
      this.setDefaults();
      this.hasTestSetup = false;
      this.alerts.success('test.delete.success', 'Test deleted');
    } catch (error) {
      this.alerts.error('test.delete.failure', 'Test could not be deleted');
    }
  });

  saveNewsletterTask = task(async () => {
    try {
      await this.validations.validateModel();
      this.newsletter.set('updateType', 'test');
      await this.newsletter.save();
    } catch {
      // We get here if `validateModel` fails and it will automatically
      // set the error messages, so we don't need to do anything here.
    }
  });

  resetTask = task(async () => {
    this.setDefaults();
    await this.newsletter.rollbackAttributes();
    this.validations.clearErrors();
    this.analytics.track('broadcast', 'edit_discarded', { model: this.newsletter, from: 'content' });
  });

  setDefaults() {
    this.isCreatingTest = false;
  }

  @action
  deleteTest() {
    // waitForPromise needed in Ember 4.12 for some reason, so _deleteTest task
    // is not cancelled early in tests.
    // test: http://127.0.0.1:4200/tests?testId=a89f8d38
    return waitForPromise(this._deleteTest.perform());
  }

  @action
  onSave() {
    return this.saveNewsletterTask.perform();
  }

  @action
  onReset() {
    return this.resetTask.perform();
  }

  @action
  setTracking(value: boolean) {
    this.newsletter.set('tracked', value);
  }
}
